/**
 * CommentEditor.jsx
 *
 * A lightweight ProseMirror editor for comment input.
 *
 * Features:
 *  - Bold (`**…**`), italic (`*…*`) input-rule shortcuts
 *  - Enter → line-break, Shift+Enter → line-break, Ctrl/Cmd+Enter → submit, Escape → blur / cancel edit
 *  - @-mention autocomplete (reuses searchUsersForMention); Enter selects mention, autocomplete blocks submit
 *  - Placeholder text
 *  - Character limit with live counter
 *  - Footer with Cancel and Comment/Reply action buttons
 *  - Imperative .clear() and .setContent(json) via React.forwardRef
 *
 * Props:
 *  - onSubmit(json)      called when user submits (receives ProseMirror doc JSON)
 *  - onCancel()          called when user clicks Cancel
 *  - initialContent      ProseMirror doc JSON to pre-populate (edit mode)
 *  - placeholder         string  (default: "Type a comment…")
 *  - submitLabel         string  (default: "Comment")
 *  - maxLength           number  (default: 2000)
 *  - autoFocus           boolean
 *  - disabled            boolean
 */

import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {EditorState, Plugin} from 'prosemirror-state';
import {Decoration, DecorationSet, EditorView} from 'prosemirror-view';
import {keymap} from 'prosemirror-keymap';
import {baseKeymap} from 'prosemirror-commands';
import {InputRule, inputRules} from 'prosemirror-inputrules';
import Button from '@mui/material/Button';
import {commentSchema} from './commentSchema';
import {createCommentAutocompletePlugin} from './commentAutocompletePlugin';
import './commentEditor.css';

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.platform);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** True if the doc contains any visible text or mention nodes */
function docIsEmpty(doc) {
    let isEmpty = true;
    doc.descendants((node) => {
        if (node.isText && node.text.trim().length > 0) {
            isEmpty = false;
            return false;
        }
        if (node.type.name === 'userMention') {
            isEmpty = false;
            return false;
        }
        return true;
    });
    return isEmpty;
}

/** Count plain-text characters in doc (mentions count as 1 char each) */
function docCharCount(doc) {
    let count = 0;
    doc.descendants((node) => {
        if (node.isText) count += node.text.length;
        else if (node.type.name === 'userMention') count += 1;
        return true;
    });
    return count;
}

/** Wrap selected text in a mark (toggle off if already applied) */
function toggleMarkCommand(markType) {
    return (state, dispatch) => {
        const {from, to} = state.selection;
        if (from === to) return false; // nothing selected
        const hasMark = state.doc.rangeHasMark(from, to, markType);
        if (dispatch) {
            const tr = hasMark
                ? state.tr.removeMark(from, to, markType)
                : state.tr.addMark(from, to, markType.create());
            dispatch(tr);
        }
        return true;
    };
}

/** Input rules for **bold** and *italic* shortcuts */
function buildInputRules() {
    const strong = commentSchema.marks.strong;
    const em = commentSchema.marks.em;

    // **text** → strong
    const boldRule = new InputRule(
        /\*\*([^*]+)\*\*$/,
        (state, match, start, end) =>
            state.tr.replaceWith(start, end, commentSchema.text(match[1], [strong.create()])),
    );

    // *text* → em  (single asterisk, not preceded by another *)
    const italicRule = new InputRule(
        /(?<!\*)\*([^*]+)\*$/,
        (state, match, start, end) =>
            state.tr.replaceWith(start, end, commentSchema.text(match[1], [em.create()])),
    );

    return inputRules({rules: [boldRule, italicRule]});
}

/** Placeholder decoration plugin — placeholderText may be a string or a React ref */
function buildPlaceholderPlugin(placeholderText) {
    return new Plugin({
        props: {
            decorations(state) {
                if (!docIsEmpty(state.doc)) return DecorationSet.empty;
                const text = typeof placeholderText === 'object' && placeholderText !== null
                    ? placeholderText.current
                    : placeholderText;
                return DecorationSet.create(state.doc, [
                    Decoration.node(0, state.doc.nodeSize - 2, {
                        'data-placeholder': text,
                        class: 'comment-editor-placeholder-block',
                    }),
                ]);
            },
        },
    });
}

/** Plugin that tracks editor focus state */
function buildFocusPlugin(onFocusChange) {
    return new Plugin({
        props: {
            handleDOMEvents: {
                focus: () => {
                    onFocusChange?.(true);
                    return false;
                },
                blur: () => {
                    onFocusChange?.(false);
                    return false;
                },
            },
        },
    });
}

function buildIsEmptyPlugin(onChange) {
    return new Plugin({
        view() {
            onChange?.(null, {isEmpty: true});
            return {
                update(view, previousState) {
                    const nextIsEmpty = docIsEmpty(view.state.doc);
                    onChange?.(view.state.doc.toJSON(), {
                        isEmpty: nextIsEmpty,
                        doc: view.state.doc,
                        docChanged: previousState ? previousState.doc !== view.state.doc : false,
                    });
                },
            };
        },
    });
}

/** Plugin that hard-blocks any transaction that would push char count over maxLength */
function buildMaxLengthPlugin(maxLength) {
    return new Plugin({
        filterTransaction(tr, state) {
            // Only check transactions that change the document
            if (!tr.docChanged) return true;
            const limit = typeof maxLength === 'object' && maxLength !== null ? maxLength.current : maxLength;
            const count = docCharCount(tr.doc);
            if (count <= limit) return true;
            // Allow deletions even if currently over limit (e.g. paste then delete)
            const prevCount = docCharCount(state.doc);
            return count <= prevCount;
        },
    });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const CommentEditor = forwardRef(function CommentEditor(
    {
        onSubmit,
        onCancel,
        initialContent = null,
        placeholder = 'Type a comment…',
        submitLabel = 'Comment',
        maxLength = 2000,
        autoFocus = false,
        disabled = false,
        submitDisabled = false,
        submitOnEnter = false,
        keepFocusOnSubmit = false,
        ariaLabel = 'Comment input',
        onChange,
        onFocusChange,
    },
    ref,
) {
    const mountRef = useRef(null);
    const viewRef = useRef(null);
    const onSubmitRef = useRef(onSubmit);
    const onCancelRef = useRef(onCancel);
    const onChangeRef = useRef(onChange);
    const onFocusChangeRef = useRef(onFocusChange);
    const disabledRef = useRef(disabled);
    const submitDisabledRef = useRef(submitDisabled);
    const keepFocusOnSubmitRef = useRef(keepFocusOnSubmit);
    const placeholderRef = useRef(placeholder);
    const maxLengthRef = useRef(maxLength);
    const submitOnEnterRef = useRef(submitOnEnter);

    // Update refs synchronously each render so ProseMirror event handlers
    // always read the latest prop values without recreating the EditorView.
    onSubmitRef.current = onSubmit;
    onCancelRef.current = onCancel;
    onChangeRef.current = onChange;
    onFocusChangeRef.current = onFocusChange;
    disabledRef.current = disabled;
    submitDisabledRef.current = submitDisabled;
    keepFocusOnSubmitRef.current = keepFocusOnSubmit;
    placeholderRef.current = placeholder;
    maxLengthRef.current = maxLength;
    submitOnEnterRef.current = submitOnEnter;

    const [isEmpty, setIsEmpty] = useState(true);
    const [isFocused, setIsFocused] = useState(false);

    const showFooter = isFocused || !isEmpty;


    // ------------------------------------------------------------------
    // Imperative API
    // ------------------------------------------------------------------
    useImperativeHandle(ref, () => ({
        clear() {
            const view = viewRef.current;
            if (!view) return;
            const {state} = view;
            const emptyDoc = commentSchema.topNodeType.createAndFill();
            const tr = state.tr.replaceWith(0, state.doc.content.size, emptyDoc.content);
            view.dispatch(tr);
        },
        setContent(json) {
            const view = viewRef.current;
            if (!view) return;
            try {
                const doc = commentSchema.nodeFromJSON(json);
                const {state} = view;
                const tr = state.tr.replaceWith(0, state.doc.content.size, doc.content);
                view.dispatch(tr);
            } catch (e) {
                console.error('[CommentEditor] setContent failed:', e);
            }
        },
        focus() {
            viewRef.current?.focus();
        },
        hasFocus() {
            return Boolean(viewRef.current?.hasFocus());
        },
    }));

    // ------------------------------------------------------------------
    // Mount / destroy EditorView
    // ------------------------------------------------------------------
    useEffect(() => {
        if (!mountRef.current) return;

        const submitCommand = (state, dispatch) => {
            if (disabledRef.current || submitDisabledRef.current || docIsEmpty(state.doc)) return false;
            if (dispatch) {
                const json = state.doc.toJSON();
                setTimeout(() => onSubmitRef.current?.(json), 0);
                if (keepFocusOnSubmitRef.current) {
                    setTimeout(() => viewRef.current?.focus(), 0);
                }
            }
            return true;
        };

        const insertHardBreak = (state, dispatch) => {
            if (dispatch) {
                const br = commentSchema.nodes.hard_break.create();
                dispatch(state.tr.replaceSelectionWith(br).scrollIntoView());
            }
            return true;
        };

        const editorKeymap = keymap({
            ...baseKeymap,
            // Ctrl/Cmd+Enter → submit
            'Mod-Enter': submitCommand,
            // Plain Enter and Shift+Enter behavior (autocomplete intercepts Enter first when dropdown is open)
            Enter: (state, dispatch) => submitOnEnterRef.current ? submitCommand(state, dispatch) : insertHardBreak(state, dispatch),
            'Shift-Enter': insertHardBreak,
            Escape: (_state, _dispatch) => {
                viewRef.current?.dom.blur();
                onCancelRef.current?.();
                return true;
            },
            'Mod-b': (state, dispatch) => toggleMarkCommand(commentSchema.marks.strong)(state, dispatch),
            'Mod-i': (state, dispatch) => toggleMarkCommand(commentSchema.marks.em)(state, dispatch),
        });

        const autocompletePlugins = createCommentAutocompletePlugin();

        let doc = null;
        if (initialContent) {
            try {
                doc = commentSchema.nodeFromJSON(initialContent);
            } catch (e) {
                console.warn('[CommentEditor] Could not parse initialContent:', e);
            }
        }

        const state = EditorState.create({
            schema: commentSchema,
            doc: doc ?? undefined,
            plugins: [
                // autocomplete must come first so its Enter handler (confirm mention)
                // takes priority over the editor's Enter (insert hard break)
                ...autocompletePlugins,
                editorKeymap,
                buildInputRules(),
                buildPlaceholderPlugin(placeholderRef),
                buildIsEmptyPlugin((json, meta = {}) => {
                    setIsEmpty(meta.isEmpty ?? true);
                    onChangeRef.current?.(json, meta);
                }),
                buildMaxLengthPlugin(maxLengthRef),
                buildFocusPlugin((focused) => {
                    setIsFocused(focused);
                    onFocusChangeRef.current?.(focused);
                }),
            ],
        });

        const view = new EditorView(mountRef.current, {
            state,
            editable: () => !disabled,
            attributes: {
                'aria-label': ariaLabel,
                'aria-multiline': 'true',
                role: 'textbox',
            },
        });

        viewRef.current = view;

        if (autoFocus) {
            setTimeout(() => view.focus(), 50);
        }

        return () => {
            view.destroy();
            viewRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Mount once

    // Update editable state when disabled prop changes
    useEffect(() => {
        if (!viewRef.current) {
            return;
        }

        viewRef.current.setProps({
            editable: () => !disabled,
            attributes: {
                'aria-label': ariaLabel,
                'aria-multiline': 'true',
                role: 'textbox',
            },
        });
        viewRef.current.updateState(viewRef.current.state);
    }, [ariaLabel, disabled]);

    // Focus when autoFocus changes to true after mount
    useEffect(() => {
        if (autoFocus && viewRef.current) {
            setTimeout(() => viewRef.current?.focus(), 50);
        }
    }, [autoFocus]);

    const handleSubmit = (e) => {
        e?.preventDefault();
        const view = viewRef.current;
        if (!view || disabled || submitDisabled || isEmpty) return;
        onSubmitRef.current?.(view.state.doc.toJSON());
        if (keepFocusOnSubmit) {
            setTimeout(() => viewRef.current?.focus(), 0);
        }
    };

    const handleCancel = (e) => {
        e?.preventDefault();
        onCancelRef.current?.();
    };

    const shortcutHint = submitOnEnter
        ? '↵ Send'
        : IS_MAC
            ? '⌘↵'
            : 'Ctrl+↵';

    return (
        <div className="comment-editor-wrapper">
            <div ref={mountRef} className="comment-editor-content"/>
            {showFooter && (
                <div className="comment-editor-footer">
                    <span className="comment-editor-shortcut">{shortcutHint}</span>
                    <div className="comment-editor-actions">
                        {onCancel && (
                            <Button
                                size="small"
                                variant="text"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={handleCancel}
                                disabled={disabled}
                            >
                                Cancel
                            </Button>
                        )}
                        <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={handleSubmit}
                            disabled={disabled || submitDisabled || isEmpty}
                        >
                            {submitLabel}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
});

export default CommentEditor;

